import { useCallback, useState } from "react";
import {
  GMAIL_MAX_RESULTS,
  GOOGLE_GMAIL_SCOPE,
  GOOGLE_OAUTH_READY,
  GoogleAuthError,
  ensureGsiLoaded,
  fetchThreadAsText,
  getThreadPreview,
  requestGoogleAccessToken,
  searchThreads,
  threadToFile,
  type GmailThreadPreview,
} from "../api";

import styles from "./GmailSearchPanel.module.css";

interface Props {
  // Called with the picked threads converted to `File` objects -
  // the parent appends them to its `files` state alongside drag-
  // dropped uploads. Each thread becomes a text/plain `.txt` File
  // tagged kind='email_thread' by the FE's `inferKind` on intake.
  onAddFiles: (files: File[]) => void;
  // Optional: panel disables itself while the parent is in a
  // submit-inflight state.
  disabled?: boolean;
}

// Search-box + result-list for picking Gmail threads to attach to
// a project. Ports the legacy `GmailSearchPanel` (frontend/app.jsx:
// 2462) but owns its own state - the parent only needs
// `onAddFiles` to plumb threads into its upload pipeline.
//
// OAuth: the operator clicks Search -> the panel ensures the
// Google Identity Services library is loaded -> requests a
// `gmail.readonly` token -> uses it for both the search call and
// every per-thread fetch in the SAME session. Token lives in this
// component's state only; never persisted, never leaves the
// browser to InRange's backend.
//
// On first use after a fresh deploy, the consent dialog asks the
// operator to grant `gmail.readonly`. If the OAuth client ID's
// JavaScript origins allowlist doesn't include the current page's
// origin, the request fails with a "redirect_uri_mismatch" / "403
// origin not allowed" error - John needs to add the origin via
// the Google Cloud Console then. See `google-config.ts` notes.
export const GmailSearchPanel = ({ onAddFiles, disabled }: Props) => {
  const [token, setToken] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [previews, setPreviews] = useState<GmailThreadPreview[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = useCallback(() => {
    setPreviews([]);
    setSelected(new Set());
    setStatus(null);
    setErr(null);
  }, []);

  // Acquire a Gmail access token, loading GSI lazily on first call.
  // The cached React-state token is reused for every Gmail call
  // within the page lifetime (`if (token) return token`). When the
  // token eventually expires (~1h), the call sites below catch 401,
  // call `forceFreshToken()` to drop the cached value, and retry
  // once. So in steady state there is exactly ONE consent prompt
  // per page load - the legacy single-file app behaved the same.
  const ensureToken = useCallback(async (): Promise<string> => {
    if (token) return token;
    await ensureGsiLoaded();
    const fresh = await requestGoogleAccessToken([GOOGLE_GMAIL_SCOPE]);
    setToken(fresh);
    return fresh;
  }, [token]);

  // Run a Gmail call; on 401, clear the cached token, re-acquire,
  // and retry once. Wraps the three call sites (search + preview +
  // fetchThreadAsText) uniformly. The underlying helpers throw with
  // an "(401):" prefix in the message string per `gmail.ts` -
  // string-matching it is simpler than refactoring the helpers to
  // expose typed errors.
  const callWith401Retry = useCallback(
    async <T,>(op: (accessToken: string) => Promise<T>): Promise<T> => {
      const accessToken = await ensureToken();
      try {
        return await op(accessToken);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        // Match the literal "(401):" prefix our gmail.ts helpers
        // emit (`Gmail search failed (401): ...`) - not a bare
        // \b401\b which would false-positive on a 5xx body whose
        // JSON happens to mention "401" in prose.
        if (!/\(401\)/.test(msg)) throw e;
        // Token expired (or revoked). Drop, re-acquire, retry once.
        setToken(null);
        await ensureGsiLoaded();
        const refreshed = await requestGoogleAccessToken([GOOGLE_GMAIL_SCOPE]);
        setToken(refreshed);
        return op(refreshed);
      }
    },
    [ensureToken],
  );

  const onSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    reset();
    setBusy(true);
    setStatus("Loading Google sign-in…");
    try {
      setStatus(`Searching Gmail for ${JSON.stringify(q)}…`);
      const stubs = await callWith401Retry((tok) => searchThreads(q, tok));
      if (stubs.length === 0) {
        setStatus("No threads matched.");
        return;
      }
      setStatus(
        `Found ${stubs.length} thread${stubs.length === 1 ? "" : "s"}; loading previews…`,
      );
      // Per-thread preview calls in parallel. GMAIL_MAX_RESULTS=25
      // is the cap so this is safely bounded.
      const previewResults = await callWith401Retry((tok) =>
        Promise.all(stubs.map((s) => getThreadPreview(s.id, tok))),
      );
      setPreviews(previewResults);
      setStatus(
        `Showing ${previewResults.length} of up to ${GMAIL_MAX_RESULTS}. Tick + click Add to attach.`,
      );
    } catch (e) {
      // User cancelled the consent popup - not an error, just a
      // silent abort. Clear status without red box.
      if (e instanceof GoogleAuthError && e.isUserCancelled) {
        setStatus(null);
      } else {
        setErr(e instanceof Error ? e.message : String(e));
        setStatus(null);
      }
    } finally {
      setBusy(false);
    }
  }, [query, reset, callWith401Retry]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onAdd = useCallback(async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setErr(null);
    setStatus(`Fetching ${selected.size} thread${selected.size === 1 ? "" : "s"}…`);
    try {
      // Sequential fetch keeps the network polite + the UI status
      // line meaningful ("Fetching N of M…"). 25-cap means this is
      // bounded at < 30s in practice.
      const files: File[] = [];
      const ids = [...selected];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        setStatus(`Fetching thread ${i + 1} of ${ids.length}…`);
        const { text, subject } = await callWith401Retry((tok) =>
          fetchThreadAsText(id, tok),
        );
        files.push(threadToFile(text, subject, id));
      }
      onAddFiles(files);
      setStatus(
        `Added ${files.length} thread${files.length === 1 ? "" : "s"} to the document set.`,
      );
      setSelected(new Set());
      // Keep results visible so the operator can pick more from the
      // same search.
    } catch (e) {
      if (e instanceof GoogleAuthError && e.isUserCancelled) {
        setStatus(null);
      } else {
        setErr(e instanceof Error ? e.message : String(e));
        setStatus(null);
      }
    } finally {
      setBusy(false);
    }
  }, [selected, onAddFiles, callWith401Retry]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void onSearch();
    }
  };

  if (!GOOGLE_OAUTH_READY) {
    return (
      <section className={styles.section}>
        <div className={styles.eyebrow}>Search Gmail</div>
        <p className={styles.note}>
          Gmail integration is not configured for this deploy. Set{" "}
          <code>GOOGLE_CLIENT_ID</code> + <code>GOOGLE_API_KEY</code> in{" "}
          <code>web/src/api/google-config.ts</code> and add the v2 CloudFront
          origin to the Google Cloud Console allowlist.
        </p>
      </section>
    );
  }

  const inflight = busy || disabled;

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <div className={styles.eyebrow}>Search Gmail</div>
        <div className={styles.title}>
          Find email threads related to this project
        </div>
        <div className={styles.note}>
          Examples: <code>BRS2 OR Avonmouth</code>,{" "}
          <code>from:nged.co.uk after:2026/01/01</code>,{" "}
          <code>subject:G99</code>. Up to {GMAIL_MAX_RESULTS} threads per
          search. Read-only — the agent only sees the threads you tick + add.
        </div>
      </div>

      <div className={styles.searchRow}>
        <input
          className={styles.searchInput}
          placeholder="Search keywords or Gmail operators (from:, subject:, after:)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={inflight}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => void onSearch()}
          disabled={inflight || !query.trim()}
          className={styles.searchBtn}
        >
          {busy ? "Working…" : token ? "Search" : "Sign in & search"}
        </button>
      </div>

      {err && <div className={styles.err}>{err}</div>}
      {status && !err && <div className={styles.status}>{status}</div>}

      {previews.length > 0 && (
        <>
          <ul className={styles.list}>
            {previews.map((r) => (
              <li key={r.id} className={styles.item}>
                <input
                  type="checkbox"
                  checked={selected.has(r.id)}
                  onChange={() => toggle(r.id)}
                  className={styles.checkbox}
                  aria-label={`Select thread ${r.subject}`}
                  disabled={inflight}
                />
                <div className={styles.itemBody}>
                  <div className={styles.subject}>{r.subject}</div>
                  <div className={styles.meta}>
                    {r.from && <span>{r.from}</span>}
                    {r.date && <span> · {r.date}</span>}
                    {r.messageCount > 1 && (
                      <span> · {r.messageCount} msgs</span>
                    )}
                  </div>
                  {r.snippet && <div className={styles.snippet}>{r.snippet}</div>}
                </div>
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            <button
              type="button"
              onClick={() => void onAdd()}
              disabled={inflight || selected.size === 0}
              className={styles.addBtn}
            >
              {busy
                ? "Working…"
                : `Add ${selected.size} thread${selected.size === 1 ? "" : "s"} to project`}
            </button>
          </div>
        </>
      )}
    </section>
  );
};
